import { useEffect, useRef, type RefObject } from 'react';

/** Фокусируемые элементы оверлея (docs/06 §4). */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Фокус-трап модалки и панели задачи (docs/06 §4): при открытии удерживает
 * фокус внутри контейнера, `Tab` ходит по кругу, `Esc` закрывает оверлей, а при
 * закрытии фокус возвращается на элемент, который был активен до открытия.
 *
 * Эффект трапа зависит только от `isOpen`. Раньше он зависел и от `onClose`,
 * который пересоздаётся на каждом рендере: эффект перезапускался после каждого
 * введённого символа и уводил фокус на первую кнопку — «Закрыть». Актуальный
 * `onClose` читается через ref.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
  onClose: () => void,
): void {
  const onCloseRef = useRef(onClose);
  const restoreRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  // Значение уже текущего рендера, а не эффекта: по нему трекер отличает
  // фокус только что открытого оверлея от фокуса на кнопке-триггере.
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Пока оверлей закрыт, запоминаем активный элемент — будущую точку возврата.
  // Слушатель, а не разовый замер: к моменту открытия `autoFocus` поля уже
  // перехватил фокус, и по `document.activeElement` триггер не найти.
  useEffect(() => {
    if (isOpen) {
      return;
    }
    const track = (target: EventTarget | null): void => {
      if (isOpenRef.current) {
        return;
      }
      if (!(target instanceof HTMLElement) || target === document.body || !target.isConnected) {
        return;
      }
      restoreRef.current = target;
    };
    track(document.activeElement);
    const onFocusIn = (event: FocusEvent): void => track(event.target);
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, [isOpen]);

  // Возврат фокуса при закрытии. Отдельный эффект: cleanup основного эффекта
  // срабатывает и на StrictMode-перезапуске, когда оверлей ещё открыт.
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      return;
    }
    if (!wasOpenRef.current) {
      return;
    }
    wasOpenRef.current = false;
    const restore = restoreRef.current;
    if (restore && document.contains(restore)) {
      restore.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const node = containerRef.current;
    // Фокус ставим на контейнер диалога, а не в первое поле или на «Закрыть»:
    // иначе Safari считает поле автозаполняемым и подсвечивает его, а на
    // мобильных открывается клавиатура. Tab сразу ведёт к первому действию.
    // Если фокус уже внутри — не перебиваем его.
    const isFocusInside = node?.contains(document.activeElement) ?? false;
    if (!isFocusInside) {
      node?.focus();
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !node) {
        return;
      }
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (!firstItem || !lastItem) {
        return;
      }
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, containerRef]);
}
